import { Reveal } from "@/components/Reveal";
import { TrendingUp, Clock, ArrowUpRight } from "lucide-react";

const studies = [
  {
    client: "Global Fintech",
    sector: "Financial Services",
    result: "Autonomous compliance agent",
    body: "Built a multi-agent system that reviews transactions and drafts regulatory reports, cutting manual review time dramatically.",
    metric: "84%",
    metricLabel: "less manual review",
    icon: TrendingUp,
  },
  {
    client: "SaaS Scale-up",
    sector: "B2B Software",
    result: "Grounded support copilot",
    body: "Deployed a RAG assistant across support and success teams, deflecting routine tickets while keeping answers cited and on-brand.",
    metric: "3.1x",
    metricLabel: "faster resolution",
    icon: Clock,
  },
  {
    client: "Industrial OEM",
    sector: "Manufacturing",
    result: "Predictive ops agents",
    body: "Engineered agents that watch telemetry, predict failures, and open work orders autonomously — before the line goes down.",
    metric: "$2.4M",
    metricLabel: "downtime avoided / yr",
    icon: TrendingUp,
  },
];

export default function CaseStudies() {
  return (
    <section id="work" className="relative bg-[#050505] py-24 lg:py-32 border-t border-white/10" data-testid="casestudies-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <Reveal>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-gradient-to-r from-[#4f6bff] to-[#9d6bff]" />
              <span className="overline text-[#8b7cff]/90">03 — Selected Work</span>
            </div>
            <h2 className="font-display font-700 tracking-tighter leading-none text-white text-4xl sm:text-5xl max-w-2xl">
              Proof, not promises.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-white/55 max-w-sm leading-relaxed">
              A snapshot of outcomes we&apos;ve delivered. Every engagement is measured against
              the numbers that matter to the business.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {studies.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.client} delay={i * 0.08} className="group">
                <div
                  className="h-full flex flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-8 hover:border-[#8b7cff]/40 hover:bg-white/[0.04] hover:-translate-y-1 transition-[transform,border-color,background-color] duration-500"
                  data-testid={`casestudy-card-${i}`}
                >
                  <div className="flex items-center justify-between mb-8">
                    <span className="overline text-white/40">{s.sector}</span>
                    <span className="h-9 w-9 rounded-full border border-white/10 flex items-center justify-center group-hover:border-[#8b7cff]/40 transition-colors duration-500">
                      <Icon className="h-4 w-4 text-[#8b7cff]" strokeWidth={1.5} />
                    </span>
                  </div>

                  <div className="mb-6">
                    <div className="font-display text-4xl font-900 tracking-tighter bg-gradient-to-r from-[#4f6bff] to-[#9d6bff] bg-clip-text text-transparent">
                      {s.metric}
                    </div>
                    <div className="mt-1 text-xs text-white/45">{s.metricLabel}</div>
                  </div>

                  <h3 className="font-display text-lg font-500 text-white tracking-tight mb-3">{s.result}</h3>
                  <p className="text-sm text-white/55 leading-relaxed flex-1">{s.body}</p>

                  <div className="mt-7 pt-5 border-t border-white/10 flex items-center justify-between">
                    <span className="text-white/70 text-sm font-500">{s.client}</span>
                    <ArrowUpRight className="h-4 w-4 text-white/40 group-hover:text-[#8b7cff] transition-colors duration-300" />
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
